import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldPlus, Heartbeat, Umbrella, FirstAidKit, UserCircle,
  ArrowRight, ChatCircleDots, CheckCircle, IdentificationCard,
  Buildings, ClipboardText, Lightning, ArrowUpRight, WhatsappLogo,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ChatbotInfographic from "@/components/insurehub/ChatbotInfographic";
import { Logo, Copyright } from "@/components/insurehub/Logo";
import GuestChatWidget from "@/components/insurehub/GuestChatWidget";

const HERO_IMG = "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwY29ycG9yYXRlJTIwdGVhbSUyMG9mZmljZXxlbnwwfHx8fDE3ODY0Njg2MDV8MA&ixlib=rb-4.1.0&q=85";
const FAMILY_IMG = "https://images.unsplash.com/photo-1588979355313-6711a095465f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBoZWFsdGglMjBwcm90ZWN0aW9ufGVufDB8fHx8MTc4NjQ2ODYwNXww&ixlib=rb-4.1.0&q=85";

const PRODUCTS = [
  {
    id: "group-health",
    icon: Heartbeat,
    tag: "Corporate",
    title: "Group Health Insurance",
    desc: "Comprehensive medical cover for your entire workforce and their families — cashless hospitalization, maternity, day-care and pre/post cover.",
    points: ["Cashless network of 10,000+ hospitals", "Family floater & dependent cover", "Zero waiting period on group plans"],
    accent: "emerald",
  },
  {
    id: "group-term-life",
    icon: Umbrella,
    tag: "Corporate",
    title: "Group Term Life",
    desc: "Financial security for employees' families with high-value life cover at group rates, offered as a core benefit of employment.",
    points: ["Sum assured up to 60× salary", "Simple enrollment, no medicals*", "Accelerated claim settlement"],
    accent: "teal",
  },
  {
    id: "group-personal-accident",
    icon: FirstAidKit,
    tag: "Corporate",
    title: "Group Personal Accident",
    desc: "24×7 worldwide protection against accidental death and disability, including weekly income benefit during recovery.",
    points: ["Accidental death & permanent disability", "Temporary total disability payouts", "Global coverage, on & off duty"],
    accent: "amber",
  },
  {
    id: "individual-health",
    icon: UserCircle,
    tag: "Personal",
    title: "Individual Health",
    desc: "Top up your corporate cover or protect your family with a personal health plan tailored to your needs and budget.",
    points: ["Flexible sum insured up to ₹1 Cr", "Portable — stays with you", "Wellness & OPD add-ons"],
    accent: "emerald",
  },
];

const ACCENTS = {
  emerald: { bg: "bg-emerald-100", text: "text-emerald-700", ring: "hover:border-emerald-400" },
  teal: { bg: "bg-teal-100", text: "text-teal-700", ring: "hover:border-teal-400" },
  amber: { bg: "bg-amber-100", text: "text-amber-700", ring: "hover:border-amber-400" },
};

function Nav({ onCta, ctaLabel }) {
  return (
    <header className="sticky top-0 z-40 bg-[#FAFAFA]/85 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Logo size={38} textClass="text-lg" />
        <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-600">
          <a href="#products" className="hover:text-emerald-900 transition-colors">Products</a>
          <a href="#assistant" className="hover:text-emerald-900 transition-colors">Assistant</a>
          <a href="#why" className="hover:text-emerald-900 transition-colors">Why us</a>
        </nav>
        <Button onClick={onCta} data-testid="nav-cta"
          className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
          {ctaLabel} <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>
    </header>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => { if (ready) setLoggedIn(!!user); }, [ready, user]);

  const primaryCta = () => {
    if (user && user.role === "admin") return navigate("/admin");
    if (user) return navigate("/chat");
    return navigate("/login");
  };
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-800">
      <Nav onCta={primaryCta} ctaLabel={loggedIn ? "Open assistant" : "Sign in"} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-semibold mb-6">
              <WhatsappLogo size={15} weight="fill" /> WhatsApp-first insurance for corporates
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-emerald-950 leading-[1.05]">
              Employee benefits,<br />
              <span className="text-emerald-600">simplified into a chat.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-lg">
              One platform for Group Health, Group Term Life, Group Personal Accident and
              Individual Health — with an AI assistant that answers every employee query instantly.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={primaryCta} data-testid="hero-cta"
                className="rounded-full h-12 px-6 bg-emerald-900 hover:bg-emerald-800 text-white text-base transition-colors">
                <ChatCircleDots size={20} className="mr-2" /> Talk to the assistant
              </Button>
              <a href="#products"
                className="inline-flex items-center gap-2 rounded-full h-12 px-6 border border-emerald-300 text-emerald-800 text-base font-medium hover:bg-emerald-50 transition-colors">
                Explore products <ArrowRight size={18} />
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-zinc-500">
              <div><span className="font-heading text-2xl font-semibold text-emerald-950">10k+</span><br/>Network hospitals</div>
              <div className="w-px h-10 bg-zinc-200" />
              <div><span className="font-heading text-2xl font-semibold text-emerald-950">4.9★</span><br/>Employee rating</div>
              <div className="w-px h-10 bg-zinc-200" />
              <div><span className="font-heading text-2xl font-semibold text-emerald-950">2 hr</span><br/>Avg. claim TAT</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="relative">
            <div className="rounded-3xl overflow-hidden border border-zinc-200 shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
              <img src={HERO_IMG} alt="Corporate team" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-zinc-200 shadow-lg p-4 max-w-[240px] hidden sm:block">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                  <ShieldPlus size={16} weight="fill" className="text-emerald-950" />
                </div>
                <span className="text-xs font-semibold text-emerald-950">Aarogya Assist Chatbot</span>
              </div>
              <p className="text-xs text-zinc-600 bg-emerald-50 rounded-lg rounded-tl-none p-2">
                Your maternity benefit is covered up to ₹50,000 ✅
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Our Products</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-emerald-950">
            Four ways we protect your people.
          </h2>
          <p className="mt-4 text-zinc-600">
            From company-wide group cover to personal plans, every product is managed and
            serviced through the same friendly chat experience.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {PRODUCTS.map((p, i) => {
            const a = ACCENTS[p.accent];
            return (
              <motion.div
                key={p.id}
                data-testid={`product-${p.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group rounded-2xl border border-zinc-200 bg-white p-8 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.10)] hover:-translate-y-1 transition-[transform,box-shadow,border-color] ${a.ring}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`w-14 h-14 rounded-xl ${a.bg} flex items-center justify-center`}>
                    <p.icon size={30} weight="duotone" className={a.text} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{p.tag}</span>
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold text-emerald-950">{p.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{p.desc}</p>
                <ul className="mt-5 space-y-2">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-zinc-700">
                      <CheckCircle size={16} weight="fill" className={a.text} /> {pt}
                    </li>
                  ))}
                </ul>
                <button onClick={primaryCta} data-testid={`product-cta-${p.id}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:gap-2.5 transition-[gap]">
                  Ask about this plan <ArrowUpRight size={16} />
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CHATBOT FEATURES INFOGRAPHIC */}
      <ChatbotInfographic />

      {/* ASSISTANT SECTION */}
      <section id="assistant" className="bg-emerald-950 text-white py-20 grain">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">The Assistant</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
              Every insurance question, answered in seconds.
            </h2>
            <p className="mt-4 text-emerald-100/70 leading-relaxed">
              Employees don't read policy PDFs. They chat. The Aarogya Assist Chatbot's AI handles
              coverage queries, e-cards, claims and hospital search — right inside a WhatsApp-style interface.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: IdentificationCard, t: "Instant digital e-health card" },
                { icon: ClipboardText, t: "Submit & track claims conversationally" },
                { icon: Buildings, t: "Find cashless network hospitals nearby" },
                { icon: Lightning, t: "AI answers on coverage & benefits 24×7" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <f.icon size={20} className="text-emerald-300" />
                  </div>
                  <span className="text-emerald-50/90">{f.t}</span>
                </div>
              ))}
            </div>
            <Button onClick={primaryCta} data-testid="assistant-cta"
              className="mt-8 rounded-full h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-base transition-colors">
              Try the assistant
            </Button>
          </div>

          <div className="relative">
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img src={FAMILY_IMG} alt="Family protected" className="w-full h-[380px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Built for HR teams", d: "A single admin dashboard to manage employees, monitor claims and track policy value in real time." },
            { n: "02", t: "Loved by employees", d: "No jargon, no waiting on hold. Just a quick chat to get exactly what they need, whenever they need it." },
            { n: "03", t: "Fast, fair claims", d: "Cashless approvals and reimbursements tracked end-to-end, with an average 2-hour turnaround." },
          ].map((w, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="rounded-2xl border border-zinc-200 bg-white p-8">
              <span className="font-mono text-sm text-emerald-500">{w.n}</span>
              <h3 className="mt-3 font-heading text-xl font-semibold text-emerald-950">{w.t}</h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{w.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl bg-emerald-900 text-white p-10 sm:p-14 text-center relative overflow-hidden grain">
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Ready to give your team better benefits?
            </h2>
            <p className="mt-4 text-emerald-100/80">
              Sign in to explore the assistant, or use a demo account to see it in action.
            </p>
            <Button onClick={primaryCta} data-testid="footer-cta"
              className="mt-8 rounded-full h-12 px-8 bg-white hover:bg-emerald-50 text-emerald-900 font-semibold text-base transition-colors">
              Get started <ArrowRight size={18} className="ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={34} />
          <div className="text-center sm:text-right space-y-1">
            <p className="text-sm text-zinc-500">Group &amp; Individual Insurance · Our team will call you back</p>
            <Copyright />
          </div>
        </div>
      </footer>

      <GuestChatWidget />
    </div>
  );
}
